import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, FileText, Mail, UserCheck, UserCog, Workflow } from 'lucide-react';
import type { InitializeProcessInput } from '@bliks-io/contracts';
import { useDepartments } from '@/api/queries/useDepartments';
import { useOrgMembers } from '@/api/queries/useOrgMembers';
import { PROCESS_ROLE_CLASSIFICATIONS } from '@/components/form/process-role-classifications';
import { downloadProcessesTemplate } from '@/lib/excel';
import {
  parseInitializeProcessesFromExcel,
  type InitializeProcessDraft
} from '@/components/bulk-upload';
import {
  createOwnerAwareProcessRowValidator,
  processDuplicateKeys
} from '../utils/draftRowValidation';
import { upsertUploadedRows } from '@/lib/draft-rows/upsertUploadedRows';
import type { DraftColumnDef } from '../components/EditableDraftTable';
import { DraftTableStep } from '../components/DraftTableStep';
import { explainerFieldsFromColumns, explainerIcon } from '../components/FieldExplainer';
import { buildMemberOptions, UserNotFoundBadge } from '../components/memberOptions';
import { anchorDisplayRows, useAnchorSeedRows } from '../useAnchorSeedRows';
import { isClearedAnchorDraft } from '../utils/flushBaselines';
import { departmentOptions, resolveDepartmentToId } from '@/lib/department-ref';

interface ProcessesStepProps {
  processes: InitializeProcessDraft[];
  onProcessesChange: (processes: InitializeProcessDraft[]) => void;
  submitError?: string;
  /**
   * Step already imported — table is seeded from the import; on Weiter new
   * rows append, edits update in place and removals delete surgically
   * (ADR 0011, third amendment)
   */
  isDone?: boolean;
  /** The step's flush baseline — an empty draft with a surviving baseline is a cleared table */
  flushBaseline?: InitializeProcessDraft[];
}

const makeEmptyProcessRow = (rowNumber: number): InitializeProcessDraft => ({
  id: crypto.randomUUID(),
  data: { name: '', description: '', department: '', ownerEmail: '', accountableEmail: '' },
  isValid: true,
  rowNumber
});

export function ProcessesStep({
  processes,
  onProcessesChange,
  submitError,
  isDone = false,
  flushBaseline
}: ProcessesStepProps) {
  const { t } = useTranslation();
  const { data: departments } = useDepartments();
  // Owner and accountable draw from different pools: the owner moderates, so
  // an external member may hold it; accountable stays internal. The owner pool
  // doubles as the validator set, which mirrors the backend resolving owner
  // emails against ANY user — so a seeded out-of-scope owner never blocks
  // Weiter.
  const { data: ownerCandidates } = useOrgMembers({
    classification: PROCESS_ROLE_CLASSIFICATIONS
  });
  const { data: internalMembers } = useOrgMembers({ classification: 'internal' });
  const { processSeeds } = useAnchorSeedRows();

  const rows = anchorDisplayRows(processes, processSeeds, isDone, flushBaseline);

  const departmentSelectOptions = useMemo(
    () => departmentOptions(departments ?? []),
    [departments]
  );
  // buildMemberOptions constructs a <PersonChip> element per member — memoize
  // so a keystroke into any cell does not rebuild every option element.
  const ownerOptions = useMemo(() => buildMemberOptions(ownerCandidates), [ownerCandidates]);
  const accountableOptions = useMemo(() => buildMemberOptions(internalMembers), [internalMembers]);

  // Same member-aware owner check the Weiter/autosave passes run (via the
  // validation context), so an unknown ownerEmail flags inline in the table.
  const validateProcessRow = useMemo(
    () =>
      createOwnerAwareProcessRowValidator(
        new Set((ownerCandidates ?? []).map((member) => member.email.toLowerCase()))
      ),
    [ownerCandidates]
  );

  const columns: DraftColumnDef<InitializeProcessInput>[] = [
    {
      field: 'name',
      headerKey: 'bulkOnboarding.excel.columns.processes.name',
      descriptionKey: 'initialSetup.columnHints.processes.name',
      required: true,
      explainer: {
        icon: explainerIcon(Workflow),
        descriptionKey: 'initialSetup.fieldExplainer.fields.processes.name'
      }
    },
    {
      field: 'description',
      headerKey: 'bulkOnboarding.excel.columns.processes.description',
      descriptionKey: 'initialSetup.columnHints.processes.description',
      required: true,
      explainer: {
        icon: explainerIcon(FileText),
        descriptionKey: 'initialSetup.fieldExplainer.fields.processes.description'
      }
    },
    {
      field: 'department',
      headerKey: 'bulkOnboarding.excel.columns.processes.department',
      descriptionKey: 'initialSetup.columnHints.processes.department',
      required: true,
      options: departmentSelectOptions,
      explainer: {
        icon: explainerIcon(Building2),
        descriptionKey: 'initialSetup.fieldExplainer.fields.processes.department'
      }
    },
    {
      field: 'ownerEmail',
      headerKey: 'initialSetup.columns.processes.owner',
      descriptionKey: 'initialSetup.columnHints.processes.owner',
      required: true,
      options: ownerOptions,
      unknownValueContent: <UserNotFoundBadge />,
      explainer: {
        icon: explainerIcon(UserCog),
        descriptionKey: 'initialSetup.fieldExplainer.fields.processes.owner'
      }
    },
    {
      field: 'accountableEmail',
      headerKey: 'initialSetup.columns.processes.accountable',
      descriptionKey: 'initialSetup.columnHints.processes.accountable',
      optional: true,
      options: accountableOptions,
      unknownValueContent: <UserNotFoundBadge />,
      explainer: {
        icon: explainerIcon(UserCheck),
        descriptionKey: 'initialSetup.fieldExplainer.fields.processes.accountable'
      }
    }
  ];

  const handleParse = async (file: File) => {
    const result = await parseInitializeProcessesFromExcel(file);
    // Resolve the sheet's department abbreviation to the stable id at the
    // import boundary; a repeat import updates matching on-screen rows (drafts
    // or seeds) in place instead of stacking duplicates.
    const resolved = result.drafts.map((draft) => ({
      ...draft,
      data: {
        ...draft.data,
        department: resolveDepartmentToId(draft.data.department, departments ?? [])
      }
    }));
    onProcessesChange(upsertUploadedRows(rows, resolved, processDuplicateKeys));
    return result.drafts.length;
  };

  return (
    <DraftTableStep
      title={t('initialSetup.processes.title')}
      description={t('initialSetup.processes.description')}
      hint={t('initialSetup.processes.hint')}
      isDone={isDone}
      rows={rows}
      draftRows={processes}
      onRowsChange={onProcessesChange}
      clearScope={isDone ? 'all' : 'drafts'}
      pendingDeleteAll={isClearedAnchorDraft(processes, flushBaseline)}
      columns={columns}
      validateRow={validateProcessRow}
      duplicateChecks={processDuplicateKeys}
      makeEmptyRow={makeEmptyProcessRow}
      addLabelKey="initialSetup.addRow.process"
      titleKey="initialSetup.tables.processes"
      uploadTitle={t('bulkOnboarding.processes.uploadTitle')}
      explainerHeadingKey="initialSetup.fieldExplainer.headings.processes"
      explainerSections={[{ fields: explainerFieldsFromColumns(columns) }]}
      downloadTemplate={downloadProcessesTemplate}
      parse={handleParse}
      submitError={submitError}
      footer={
        <div className="mt-3.5 flex max-w-[82ch] items-start gap-2.5 text-[12.5px] leading-[1.55] text-fg-secondary">
          <Mail className="mt-px h-[15px] w-[15px] shrink-0 text-fg-subtle" strokeWidth={1.8} />
          <span>{t('initialSetup.processes.mailImportNote')}</span>
        </div>
      }
    />
  );
}
